import { useEffect } from 'react';
import {
  ObjectiveFilter,
  ObjectiveFilterManager,
  StringUpdateInput,
  useAllObjectivesLazyQuery,
  useMyColleaguesObjectivesLazyQuery,
  useMyObjectivesLazyQuery,
  useMyReportsObjectivesLazyQuery,
} from '../../../__generated__/graphql';
import { GoalFilter } from './GoalsFilter/GoalsFilter';
import { useGoalsContext } from './GoalsContext/GoalsContext';
import { mapGoals } from './mappings';
import { GoalBase, GoalsFilterType, GoalType, NULL_CATEGORY_ID } from './types';

export const useObjectives = (
  // must be memoized
  filter: GoalFilter,
  onlyTypes: GoalType[] | 'all',
) => {
  const { developmentFrameworkEnabled, currentPeriodId } = useGoalsContext();

  const filterType: GoalsFilterType = filter.filterType;

  const objectivePeriodId = getEffectivePeriodId(
    filter,
    developmentFrameworkEnabled,
    currentPeriodId,
  );

  const [getMyObjectives, myObjectivesResults] = useMyObjectivesLazyQuery({
    variables: {
      filter: toObjectiveFilter(
        filter,
        developmentFrameworkEnabled,
        currentPeriodId,
      ),
      objectivePeriodId,
    },
    notifyOnNetworkStatusChange: true,
  });
  const [getColleaguesObjectives, myColleaguesObjectiveResults] =
    useMyColleaguesObjectivesLazyQuery({
      notifyOnNetworkStatusChange: true,
    });

  const [getAllObjectives, allObjectivesResults] = useAllObjectivesLazyQuery({
    notifyOnNetworkStatusChange: true,
  });

  const [getMyReportsObjectives, myReportsObjectivesResult] =
    useMyReportsObjectivesLazyQuery({
      notifyOnNetworkStatusChange: true,
    });

  const filterByGoalType = (goal: { type: GoalType }) => {
    if (onlyTypes === 'all') {
      return true;
    } else {
      return onlyTypes.includes(goal.type);
    }
  };

  const filterByStatus = (goal: GoalBase) => {
    if (!filter.status) {
      return true;
    }
    return goal.status === filter.status;
  };

  const applyFilters = (goals: GoalBase[] | undefined) => {
    return goals?.filter(filterByGoalType).filter(filterByStatus);
  };
  useEffect(() => {
    switch (filterType) {
      case 'myGoals':
        getMyObjectives();
        break;
      case 'colleaguesGoals':
        getColleaguesObjectives({
          variables: {
            filter: toObjectiveFilter(
              filter,
              developmentFrameworkEnabled,
              currentPeriodId,
            ),
            objectivePeriodId,
          },
        });
        break;
      case 'allGoals':
        getAllObjectives({
          variables: {
            filter: toObjectiveFilterManager(
              filter,
              developmentFrameworkEnabled,
              currentPeriodId,
            ),
            objectivePeriodId,
          },
        });
        break;
      case 'myReportsGoals':
        getMyReportsObjectives({
          variables: {
            filter: toObjectiveFilterManager(
              filter,
              developmentFrameworkEnabled,
              currentPeriodId,
            ),
            objectivePeriodId,
          },
        });
        break;
      default:
        throw new Error(`Unknown filter type: ${filterType}`);
    }
  }, [
    filter,
    filterType,
    developmentFrameworkEnabled,
    currentPeriodId,
    objectivePeriodId,
    getMyObjectives,
    getColleaguesObjectives,
    getAllObjectives,
    getMyReportsObjectives,
  ]);

  switch (filterType) {
    case 'myGoals': {
      return {
        ...myObjectivesResults,
        goals: applyFilters(
          mapGoals(myObjectivesResults.data?.me.myObjectives),
        ),
      };
    }
    case 'colleaguesGoals':
      return {
        ...myColleaguesObjectiveResults,
        goals: applyFilters(
          mapGoals(myColleaguesObjectiveResults.data?.me.colleaguesObjectives),
        ),
      };
    case 'allGoals':
      return {
        ...allObjectivesResults,
        goals: applyFilters(mapGoals(allObjectivesResults.data?.allObjectives)),
      };
    case 'myReportsGoals':
      return {
        ...myReportsObjectivesResult,
        goals: applyFilters(
          mapGoals(myReportsObjectivesResult.data?.manager.employeesObjectives),
        ),
      };
    default:
      throw new Error(`Unknown filter type: ${filterType}`);
  }
};

const toObjectiveFilter = (
  src: GoalFilter,
  developmentFrameworkEnabled: boolean,
  currentPeriodId: string | null,
): ObjectiveFilter => ({
  tagId: src.tagId,
  periodId:
    developmentFrameworkEnabled && currentPeriodId
      ? currentPeriodId
      : // src.periodId for back compatibility
        src.periodId,
  employeeId: src.employeeId,
  goalType: src.goalType,
  categoryId: toCategoryIdStringUpdateInput(src.categoryId),
  isRoot: null,
});

const toObjectiveFilterManager = (
  src: GoalFilter,
  developmentFrameworkEnabled: boolean,
  currentPeriodId: string | null,
): ObjectiveFilterManager => ({
  tagId: src.tagId,
  periodId:
    developmentFrameworkEnabled && currentPeriodId
      ? currentPeriodId
      : // todo: src.periodId for back compatibility, we need to think how to remove it
        src.periodId,
  employeeId: src.employeeId,
  //todo: check location filter
  locationId: src.locationId,
  goalType: src.goalType,
  categoryId: toCategoryIdStringUpdateInput(src.categoryId),
  isRoot: null,
  approvalStatus: src.approvalStatus,
});

const getEffectivePeriodId = (
  filter: GoalFilter,
  developmentFrameworkEnabled: boolean,
  currentPeriodId: string | null,
): string =>
  (developmentFrameworkEnabled && currentPeriodId
    ? currentPeriodId
    : filter.periodId) ?? '';

const toCategoryIdStringUpdateInput = (
  categoryId: string | null,
): StringUpdateInput | null => {
  if (categoryId === null) {
    return null;
  }
  if (categoryId === NULL_CATEGORY_ID) {
    return { value: null };
  }
  return { value: categoryId };
};
